import React, { useState, useEffect } from 'react';
import AdminPanelHeader from '../../components/common/Header/AdminPanelHeader';
import { apiRequest } from '../../utils/apiHelper';
import Footer from '../../components/common/Footer/Footer';
import { 
  Users, Calendar, Search, Filter, Download, Plus, Edit, Trash2, 
  Eye, AlertCircle, CheckCircle, X, FileText, TrendingUp, Clock, Mail, Phone,
  ChevronDown, ChevronUp, RefreshCw, Settings, Activity, Save,
  GripVertical, ArrowUp, ArrowDown, Copy, Play
} from 'lucide-react';

const RiskProfileAdminDashboard = () => {
  // API base URL
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // State for responses data
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // State for statistics
  const [statistics, setStatistics] = useState({
    totalResponses: 0,
    todayResponses: 0,
    weekResponses: 0,
    monthResponses: 0,
    riskProfileDistribution: {}
  });

  // State for filters
  const [currentFilter, setCurrentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, name, score
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // State for UI
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [expandedResponse, setExpandedResponse] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // State for form management
  const [showFormManager, setShowFormManager] = useState(false);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [customForms, setCustomForms] = useState([]);
  const [editingForm, setEditingForm] = useState(null);

  // Form Builder States
  const [formData, setFormData] = useState({
    formName: '',
    description: '',
    isActive: true,
    isDefault: false
  });
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [activeTab, setActiveTab] = useState('builder');

  // Fetch all data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch initial data
  const fetchInitialData = async () => {
    setInitialLoading(true);
    try {
      await Promise.all([
        fetchResponses(),
        fetchStatistics(),
        fetchCustomForms()
      ]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      showMessage('Failed to load dashboard data', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  // Sort responses function
  const sortResponses = (responsesData, sortOption) => {
    const sorted = [...responsesData];
    
    switch (sortOption) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'name':
        return sorted.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
      case 'score':
        return sorted.sort((a, b) => b.totalScore - a.totalScore);
      default:
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    const sortedResponses = sortResponses(responses, newSortBy);
    setResponses(sortedResponses);
  };

  // Fetch responses based on filter
  const fetchResponses = async (filter = 'all') => {
    setLoading(true);
    try {
      let url;
      if (filter === 'all') {
        url = `${API_BASE_URL}/risk-profile/admin/responses`;
      } else if (['today', 'week', 'month'].includes(filter)) {
        url = `${API_BASE_URL}/risk-profile/admin/responses/filter?filter=${filter}`;
      } else if (filter === 'search' && searchTerm) {
        url = `${API_BASE_URL}/risk-profile/admin/responses/search?searchTerm=${searchTerm}`;
      } else if (filter === 'dateRange' && dateRange.startDate && dateRange.endDate) {
        url = `${API_BASE_URL}/risk-profile/admin/responses/date-range?startDate=${dateRange.startDate}T00:00:00&endDate=${dateRange.endDate}T23:59:59`;
      } else {
        url = `${API_BASE_URL}/risk-profile/admin/responses`;
      }

      const response = await apiRequest(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      const responsesData = data.success ? data.data : [];
      
      // Sort responses based on selected sort option
      const sortedResponses = sortResponses(responsesData, sortBy);
      setResponses(sortedResponses);
    } catch (error) {
      console.error('Error fetching responses:', error);
      showMessage('Failed to fetch responses', 'error');
      setResponses([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/risk-profile/admin/statistics`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      if (data.success) {
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      showMessage('Failed to fetch statistics', 'error');
    }
  };

  // Fetch custom forms
  const fetchCustomForms = async () => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/questionnaire-forms`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      setCustomForms(data.success ? data.data : []);
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  // Handle filter change
  const handleFilterChange = (filter) => {
    setCurrentFilter(filter);
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    fetchResponses(filter);
  };

  // Handle search
  const handleSearch = () => {
    if (searchTerm.trim()) {
      setCurrentFilter('search');
      fetchResponses('search');
    }
  };

  // Handle date range filter
  const handleDateRangeFilter = () => {
    if (dateRange.startDate && dateRange.endDate) {
      setCurrentFilter('dateRange');
      fetchResponses('dateRange');
    }
  };

  // Show response details
  const showResponseDetails = async (responseId) => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/risk-profile/admin/responses/${responseId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      if (data.success) {
        setSelectedResponse(data.data);
        setShowResponseModal(true);
      }
    } catch (error) {
      console.error('Error fetching response details:', error);
      showMessage('Failed to fetch response details', 'error');
    }
  };

  // Export data
  const exportData = async (format = 'json') => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/risk-profile/admin/export?format=${format}&filter=${currentFilter}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      
      // Create and download file
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `risk-profile-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showMessage('Data exported successfully!', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showMessage('Failed to export data', 'error');
    }
  };

  // Form Builder Functions
  const openFormBuilder = (form = null) => {
    setEditingForm(form);
    if (form) {
      loadFormData(form.id);
    } else {
      // Reset for new form
      setFormData({
        formName: '',
        description: '',
        isActive: true,
        isDefault: false
      });
      setQuestions([createNewQuestion(1)]);
    }
    setShowFormBuilder(true);
    setShowFormManager(false);
  };

  const loadFormData = async (formId) => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/questionnaire-forms/${formId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      if (data.success) {
        const form = data.data;
        setFormData({
          formName: form.formName,
          description: form.description || '',
          isActive: form.isActive,
          isDefault: form.isDefault
        });
        setQuestions(form.questions || []);
      }
    } catch (error) {
      console.error('Error loading form:', error);
      showMessage('Failed to load form data', 'error');
    }
  };

  const createNewQuestion = (order) => ({
    id: null,
    questionOrder: order,
    questionText: '',
    questionType: 'SINGLE_CHOICE',
    options: [
      { id: null, optionOrder: 1, optionText: '', optionScore: 1 },
      { id: null, optionOrder: 2, optionText: '', optionScore: 2 }
    ]
  });

  const addQuestion = () => {
    const newQuestion = createNewQuestion(questions.length + 1);
    setQuestions([...questions, newQuestion]);
    setExpandedQuestion(questions.length);
  };

  const removeQuestion = (index) => {
    if (questions.length <= 1) {
      showMessage('Form must have at least one question', 'error');
      return;
    }
    
    const updatedQuestions = questions.filter((_, i) => i !== index);
    const reorderedQuestions = updatedQuestions.map((q, i) => ({
      ...q,
      questionOrder: i + 1
    }));
    setQuestions(reorderedQuestions);
    
    if (expandedQuestion === index) {
      setExpandedQuestion(null);
    } else if (expandedQuestion > index) {
      setExpandedQuestion(expandedQuestion - 1);
    }
  };

  const updateQuestion = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setQuestions(updatedQuestions);
  };

  const addOption = (questionIndex) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const newOption = {
      id: null,
      optionOrder: question.options.length + 1,
      optionText: '',
      optionScore: question.options.length + 1
    };
    question.options.push(newOption);
    setQuestions(updatedQuestions);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    
    if (question.options.length <= 2) {
      showMessage('Question must have at least 2 options', 'error');
      return;
    }
    
    question.options = question.options.filter((_, i) => i !== optionIndex);
    question.options = question.options.map((option, i) => ({
      ...option,
      optionOrder: i + 1
    }));
    
    setQuestions(updatedQuestions);
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex] = {
      ...updatedQuestions[questionIndex].options[optionIndex],
      [field]: value
    };
    setQuestions(updatedQuestions);
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.formName.trim()) {
      errors.push('Form name is required');
    }
    
    if (questions.length === 0) {
      errors.push('Form must have at least one question');
    }
    
    questions.forEach((question, qIndex) => {
      if (!question.questionText.trim()) {
        errors.push(`Question ${qIndex + 1} text is required`);
      }
      
      if (question.options.length < 2) {
        errors.push(`Question ${qIndex + 1} must have at least 2 options`);
      }
      
      question.options.forEach((option, oIndex) => {
        if (!option.optionText.trim()) {
          errors.push(`Question ${qIndex + 1}, Option ${oIndex + 1} text is required`);
        }
        
        if (!option.optionScore || option.optionScore < 1) {
          errors.push(`Question ${qIndex + 1}, Option ${oIndex + 1} must have a valid score (1 or higher)`);
        }
      });
    });
    
    return errors;
  };

  const saveForm = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showMessage(validationErrors[0], 'error');
      return;
    }
    
    setSaving(true);
    try {
      const formPayload = {
        ...formData,
        questions: questions.map(q => ({
          ...q,
          options: q.options.map(opt => ({ ...opt }))
        }))
      };
      
      let response;
      if (editingForm) {
        response = await apiRequest(`${API_BASE_URL}/questionnaire-forms/${editingForm.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formPayload)
        });
      } else {
        response = await apiRequest(`${API_BASE_URL}/questionnaire-forms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formPayload)
        });
      }
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const result = await response.json();
      if (result.success) {
        showMessage(
          `Form ${editingForm ? 'updated' : 'created'} successfully!`, 
          'success'
        );
        
        // Refresh forms list and close builder
        await fetchCustomForms();
        setShowFormBuilder(false);
        setShowFormManager(true);
      } else {
        throw new Error(result.message || 'Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      showMessage('Failed to save form: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) {
      return;
    }
    
    try {
      const response = await apiRequest(`${API_BASE_URL}/questionnaire-forms/${formId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const result = await response.json();
      if (result.success) {
        showMessage('Form deleted successfully!', 'success');
        await fetchCustomForms();
      } else {
        throw new Error(result.message || 'Failed to delete form');
      }
    } catch (error) {
      console.error('Error deleting form:', error);
      showMessage('Failed to delete form: ' + error.message, 'error');
    }
  };

  const calculateMaxScore = () => {
    return questions.reduce((total, question) => {
      const maxQuestionScore = Math.max(...question.options.map(opt => opt.optionScore || 0));
      return total + maxQuestionScore;
    }, 0);
  };

  // Show message. Success auto-dismisses after 5s; errors stay until the user closes them.
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    if (type === 'success') {
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  // Helper function to check if response is recent (within 24 hours)
  const isRecentResponse = (createdAt) => {
    const now = new Date();
    const responseDate = new Date(createdAt);
    const diffInHours = (now - responseDate) / (1000 * 60 * 60);
    return diffInHours <= 24;
  };

  // Get risk profile color
  const getRiskProfileColor = (profile) => {
    const colors = {
      'Secure': '#10b981',
      'Defensive': '#3b82f6', 
      'Conservative': '#8b5cf6',
      'Balanced': '#f59e0b',
      'Growth': '#ef4444',
      'High Growth': '#dc2626'
    };
    return colors[profile] || '#6b7280';
  };

  if (initialLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <RefreshCw size={24} className="animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  // Render Form Builder
  const renderFormBuilder = () => (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1200px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Form Builder Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#1a2332',
          color: 'white'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
            {editingForm ? 'Edit Form' : 'Create New Form'}
          </h2>
          
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>
              Max Score: {calculateMaxScore()}
            </span>
            
            <button
              onClick={() => setActiveTab('preview')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#8b5cf6',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Eye size={16} />
              Preview
            </button>
            
            <button
              onClick={saveForm}
              disabled={saving}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#10b981',
                color: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Form
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                setShowFormBuilder(false);
                setShowFormManager(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'white'
              }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Sidebar */}
          <div style={{
            width: '300px',
            borderRight: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            padding: '1.5rem',
            overflowY: 'auto'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                Form Name *
              </label>
              <input
                type="text"
                value={formData.formName}
                onChange={(e) => setFormData(prev => ({ ...prev, formName: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter form name..."
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  minHeight: '80px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                placeholder="Describe your form..."
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  style={{ marginRight: '0.5rem' }}
                />
                Active Form
              </label>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                  style={{ marginRight: '0.5rem' }}
                />
                Set as Default Form
              </label>
            </div>

            <div style={{ 
              marginTop: '2rem', 
              padding: '1rem', 
              backgroundColor: '#eff6ff', 
              borderRadius: '8px',
              border: '1px solid #bfdbfe'
            }}>
              <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#1e40af' }}>
                Form Statistics
              </h4>
              <div style={{ fontSize: '0.75rem', color: '#1e40af' }}>
                <div>Questions: {questions.length}</div>
                <div>Total Options: {questions.reduce((sum, q) => sum + q.options.length, 0)}</div>
                <div>Max Score: {calculateMaxScore()}</div>
              </div>
            </div>
          </div>

          {/* Main Form Builder Content */}
          <div style={{ flex: 1, padding: '1.5rem', overflowY: 'auto' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
              <button
                onClick={() => setActiveTab('builder')}
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  borderBottom: activeTab === 'builder' ? '2px solid #0ea5e9' : '2px solid transparent',
                  color: activeTab === 'builder' ? '#0ea5e9' : '#6b7280'
                }}
              >
                <Settings size={16} style={{ marginRight: '0.5rem' }} />
                Form Builder
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  borderBottom: activeTab === 'preview' ? '2px solid #0ea5e9' : '2px solid transparent',
                  color: activeTab === 'preview' ? '#0ea5e9' : '#6b7280'
                }}
              >
                <Eye size={16} style={{ marginRight: '0.5rem' }} />
                Preview
              </button>
            </div>

            {/* Builder Tab Content */}
            {activeTab === 'builder' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                    Questions ({questions.length})
                  </h3>
                  <button
                    onClick={addQuestion}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Plus size={16} />
                    Add Question
                  </button>
                </div>

                {questions.map((question, questionIndex) => (
                  <div 
                    key={questionIndex} 
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      backgroundColor: 'white',
                      overflow: 'hidden'
                    }}
                  >
                    <div 
                      style={{
                        padding: '1rem',
                        backgroundColor: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => setExpandedQuestion(
                        expandedQuestion === questionIndex ? null : questionIndex
                      )}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <GripVertical size={16} color="#6b7280" />
                        <span style={{ fontWeight: '600' }}>
                          Question {questionIndex + 1}
                        </span>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {question.questionText || 'Untitled Question'}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeQuestion(questionIndex);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            color: '#ef4444'
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                        
                        {expandedQuestion === questionIndex ? (
                          <ChevronUp size={16} color="#6b7280" />
                        ) : (
                          <ChevronDown size={16} color="#6b7280" />
                        )}
                      </div>
                    </div>

                    {expandedQuestion === questionIndex && (
                      <div style={{ padding: '1rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                            Question Text *
                          </label>
                          <textarea
                            value={question.questionText}
                            onChange={(e) => updateQuestion(questionIndex, 'questionText', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '1rem',
                              minHeight: '80px',
                              boxSizing: 'border-box'
                            }}
                            placeholder="Enter your question..."
                          />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                            Question Type
                          </label>
                          <select
                            value={question.questionType}
                            onChange={(e) => updateQuestion(questionIndex, 'questionType', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '1rem',
                              backgroundColor: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="SINGLE_CHOICE">Single Choice</option>
                            <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                            <option value="TEXT">Text Input</option>
                          </select>
                        </div>

                        {question.questionType !== 'TEXT' && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Answer Options</label>
                              <button
                                onClick={() => addOption(questionIndex)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                              >
                                <Plus size={14} />
                                Add Option
                              </button>
                            </div>

                            {question.options.map((option, optionIndex) => (
                              <div key={optionIndex} style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                padding: '0.75rem',
                                marginBottom: '0.75rem',
                                backgroundColor: '#fafbfc'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '0.5rem'
                                }}>
                                  <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                                    Option {optionIndex + 1}
                                  </span>
                                  <button
                                    onClick={() => removeOption(questionIndex, optionIndex)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0.25rem',
                                      color: '#ef4444'
                                    }}
                                    title="Remove Option"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    value={option.optionText}
                                    onChange={(e) => updateOption(questionIndex, optionIndex, 'optionText', e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '0.75rem',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '1rem',
                                      boxSizing: 'border-box'
                                    }}
                                    placeholder="Enter option text..."
                                  />
                                  
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Score:</label>
                                    <input
                                      type="number"
                                      value={option.optionScore}
                                      onChange={(e) => updateOption(questionIndex, optionIndex, 'optionScore', parseInt(e.target.value) || 1)}
                                      style={{
                                        width: '80px',
                                        padding: '0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        boxSizing: 'border-box'
                                      }}
                                      min="1"
                                      max="10"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {questions.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    color: '#6b7280',
                    border: '2px dashed #d1d5db',
                    borderRadius: '8px'
                  }}>
                    <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                      No questions yet
                    </h3>
                    <p style={{ marginBottom: '1.5rem' }}>
                      Add your first question to get started building your form.
                    </p>
                    <button
                      onClick={addQuestion}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: '#0ea5e9',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        margin: '0 auto'
                      }}
                    >
                      <Plus size={16} />
                      Add First Question
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Preview Tab Content */}
            {activeTab === 'preview' && (
              <div>
                <div style={{
                  marginBottom: '2rem',
                  padding: '1rem',
                  backgroundColor: '#eff6ff',
                  borderRadius: '8px',
                  border: '1px solid #bfdbfe'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.25rem', fontWeight: '600', color: '#1e40af' }}>
                        Form Preview: {formData.formName || 'Untitled Form'}
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                        {formData.description || 'No description provided'}
                      </p>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                      <div>Questions: {questions.length}</div>
                      <div>Max Score: {calculateMaxScore()}</div>
                    </div>
                  </div>
                </div>

                {questions.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    color: '#6b7280',
                    border: '2px dashed #d1d5db',
                    borderRadius: '8px'
                  }}>
                    <Eye size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                      No questions to preview
                    </h3>
                    <p>Add some questions in the Form Builder tab to see the preview.</p>
                  </div>
                ) : (
                  <div>
                    {questions.map((question, index) => (
                      <div 
                        key={index}
                        style={{
                          marginBottom: '2rem',
                          padding: '1.5rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          backgroundColor: 'white'
                        }}
                      >
                        <h4 style={{
                          margin: '0 0 1rem',
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#1a1a1a'
                        }}>
                          {index + 1}. {question.questionText || 'Untitled Question'}
                        </h4>

                        {question.questionType === 'TEXT' ? (
                          <textarea
                            placeholder="User will type their answer here..."
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '1rem',
                              minHeight: '80px',
                              backgroundColor: '#f9fafb',
                              cursor: 'not-allowed',
                              boxSizing: 'border-box'
                            }}
                            disabled
                          />
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {question.options.map((option, optionIndex) => (
                              <label
                                key={optionIndex}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  padding: '0.75rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  backgroundColor: '#fafbfc'
                                }}
                              >
                                <input
                                  type={question.questionType === 'SINGLE_CHOICE' ? 'radio' : 'checkbox'}
                                  name={`preview-question-${index}`}
                                  style={{ cursor: 'pointer' }}
                                  disabled
                                />
                                <span style={{ flex: 1 }}>
                                  {option.optionText || `Option ${optionIndex + 1}`}
                                </span>
                                <span style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  backgroundColor: '#e5e7eb',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px'
                                }}>
                                  Score: {option.optionScore}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render Form Manager Modal
  const renderFormManager = () => (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#1a2332',
          color: 'white'
        }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
            Manage Questionnaire Forms
          </h3>
          <button
            onClick={() => setShowFormManager(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              color: 'white'
            }}
          >
            <X size={24} />
          </button>
        </div>
        
        <div style={{ padding: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
              Available Forms ({customForms.length})
            </h4>
            <button
              onClick={() => openFormBuilder()}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: '#0ea5e9',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Plus size={16} />
              Create New Form
            </button>
          </div>

          {customForms.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem',
              color: '#6b7280',
              border: '2px dashed #d1d5db',
              borderRadius: '8px'
            }}>
              <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                No custom forms created yet
              </h3>
              <p style={{ marginBottom: '1.5rem' }}>
                Create your first custom questionnaire form to get started.
              </p>
              <button
                onClick={() => openFormBuilder()}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#0ea5e9',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  margin: '0 auto'
                }}
              >
                <Plus size={16} />
                Create First Form
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {customForms.map((form) => (
                <div 
                  key={form.id}
                  style={{
                    padding: '1.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: form.isDefault ? '#f0fdf4' : '#ffffff',
                    transition: 'box-shadow 0.2s ease'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <h5 style={{ 
                          margin: 0, 
                          fontSize: '1.25rem',
                          fontWeight: '600',
                          color: '#1a1a1a'
                        }}>
                          {form.formName}
                        </h5>
                        {form.isDefault && (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#22c55e',
                            color: 'white',
                            fontSize: '0.75rem',
                            borderRadius: '12px',
                            fontWeight: '600'
                          }}>
                            DEFAULT
                          </span>
                        )}
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: form.isActive ? '#dcfce7' : '#fee2e2',
                          color: form.isActive ? '#166534' : '#991b1b',
                          fontSize: '0.75rem',
                          borderRadius: '12px',
                          fontWeight: '500'
                        }}>
                          {form.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      <p style={{ 
                        margin: '0 0 0.75rem', 
                        color: '#6b7280',
                        fontSize: '0.875rem',
                        lineHeight: 1.5
                      }}>
                        {form.description || 'No description provided'}
                      </p>
                      
                      <div style={{ 
                        fontSize: '0.75rem',
                        color: '#9ca3af',
                        display: 'flex',
                        gap: '1rem'
                      }}>
                        <span>Created: {new Date(form.createdAt).toLocaleDateString()}</span>
                        {form.updatedAt && (
                          <span>Updated: {new Date(form.updatedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                      <button
                        style={{
                          padding: '0.5rem',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: '#eff6ff',
                          color: '#0ea5e9',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '36px',
                          height: '36px'
                        }}
                        title="View Details"
                        onClick={() => {
                          // Show form details
                          showMessage('Form details view coming soon!', 'success');
                        }}
                      >
                        <Eye size={16} />
                      </button>
                      
                      <button
                        style={{
                          padding: '0.5rem',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '36px',
                          height: '36px'
                        }}
                        title="Edit Form"
                        onClick={() => openFormBuilder(form)}
                      >
                        <Edit size={16} />
                      </button>
                      
                      {!form.isDefault && (
                        <button
                          style={{
                            padding: '0.5rem',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: '#fee2e2',
                            color: '#991b1b',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px'
                          }}
                          title="Delete Form"
                          onClick={() => deleteForm(form.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Form Statistics */}
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', fontSize: '0.875rem' }}>
                      <div>
                        <span style={{ color: '#6b7280' }}>Questions:</span>
                        <span style={{ fontWeight: '600', marginLeft: '0.5rem', color: '#1a1a1a' }}>
                          {form.questions?.length || 0}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Type:</span>
                        <span style={{ fontWeight: '600', marginLeft: '0.5rem', color: '#1a1a1a' }}>
                          Risk Profile
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>Version:</span>
                        <span style={{ fontWeight: '600', marginLeft: '0.5rem', color: '#1a1a1a' }}>
                          1.0
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .response-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }
        
        .quick-filter-button:hover {
          background-color: #f3f4f6;
        }
        
        .action-button:hover {
          transform: scale(1.1);
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          
          .filters-grid {
            grid-template-columns: 1fr !important;
          }
          
          .quick-filters {
            flex-direction: column !important;
          }
          
          .responses-header {
            flex-direction: column !important;
            align-items: stretch !important;
          }
        }
      `}</style>
      
        <AdminPanelHeader />
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8fafc'
      }}>
        {/* Toast — fixed top-center, in the user's eyeline */}
        <style>{`
          @keyframes rpToastSlideDown {
            from { transform: translate(-50%, -120%); opacity: 0; }
            to   { transform: translate(-50%, 0);     opacity: 1; }
          }
        `}</style>
        {message.text && (
          <div style={{
            position: 'fixed',
            top: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1500,
            padding: '0.875rem 1.25rem',
            borderRadius: '8px',
            fontWeight: '500',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            minWidth: '320px',
            maxWidth: 'min(640px, calc(100vw - 2rem))',
            animation: 'rpToastSlideDown 0.28s ease-out',
            backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: message.type === 'success' ? '1px solid #a7f3d0' : '1px solid #fca5a5'
          }}>
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
            <button
              onClick={() => setMessage({ text: '', type: '' })}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginLeft: '0.5rem'
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Response Details Modal */}
        {showResponseModal && selectedResponse && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#1a2332',
                color: 'white'
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                  Response Details - {selectedResponse.firstName} {selectedResponse.lastName}
                </h3>
                <button
                  onClick={() => setShowResponseModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    color: 'white'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                  <div>
                    <strong>Email:</strong> {selectedResponse.email}
                  </div>
                  <div>
                    <strong>Phone:</strong> {selectedResponse.phone}
                  </div>
                  <div>
                    <strong>Total Score:</strong> {selectedResponse.totalScore}
                  </div>
                  <div>
                    <strong>Risk Profile:</strong> 
                    <span style={{ 
                      color: getRiskProfileColor(selectedResponse.riskProfile),
                      fontWeight: 'bold',
                      marginLeft: '0.5rem'
                    }}>
                      {selectedResponse.riskProfile}
                    </span>
                  </div>
                  <div>
                    <strong>Allocation:</strong> {selectedResponse.allocation}
                  </div>
                  <div>
                    <strong>Submitted:</strong> {new Date(selectedResponse.createdAt).toLocaleString()}
                  </div>
                </div>
                
                {selectedResponse.description && (
                  <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <strong>Risk Profile Description:</strong>
                    <p style={{ margin: '0.5rem 0 0', lineHeight: 1.6 }}>{selectedResponse.description}</p>
                  </div>
                )}
                
                {selectedResponse.answers && selectedResponse.answers.length > 0 && (
                  <div>
                    <h4 style={{ marginBottom: '1rem' }}>Detailed Answers:</h4>
                    {selectedResponse.answers.map((answer, index) => (
                      <div key={answer.id || index} style={{ 
                        marginBottom: '1rem', 
                        padding: '1rem', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px' 
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                          Q{answer.questionId}: {answer.questionText}
                        </div>
                        <div style={{ color: '#0ea5e9', fontWeight: '500' }}>
                          Answer: {answer.selectedOptionText} (Score: {answer.optionScore})
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form Builder Modal */}
        {showFormBuilder && renderFormBuilder()}

        {/* Form Manager Modal */}
        {showFormManager && renderFormManager()}

        {/* Header */}
        <section style={{
          background: '#1a2332',
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
            radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px, 30px 30px',
          padding: '2rem 0',
          marginBottom: '0',
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 2rem',
            position: 'relative',
            zIndex: 2
          }}>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: '#ffffff',
              margin: '0',
              textAlign: 'center',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}            >
              Risk Profile Admin Dashboard
            </h1>
          </div>
        </section>

        {/* Main Content */}
        <main style={{
          backgroundColor: '#f8fafc',
          padding: '2rem 0',
          flex: 1
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 2rem'
          }}>
            {/* Statistics Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }} className="stats-grid">
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    marginBottom: '0.25rem',
                    color: '#0ea5e9'
                  }}>
                    {statistics.totalResponses}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>Total Responses</div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#eff6ff'
                }}>
                  <Users size={24} color="#0ea5e9" />
                </div>
              </div>
              
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    marginBottom: '0.25rem',
                    color: '#10b981'
                  }}>
                    {statistics.todayResponses}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>Today's Responses</div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ecfdf5'
                }}>
                  <Activity size={24} color="#10b981" />
                </div>
              </div>
              
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    marginBottom: '0.25rem',
                    color: '#8b5cf6'
                  }}>
                    {statistics.weekResponses}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>This Week</div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f3e8ff'
                }}>
                  <Calendar size={24} color="#8b5cf6" />
                </div>
              </div>
              
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    marginBottom: '0.25rem',
                    color: '#f59e0b'
                  }}>
                    {statistics.monthResponses}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>This Month</div>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fef3c7'
                }}>
                  <TrendingUp size={24} color="#f59e0b" />
                </div>
              </div>
            </div>

            {/* Risk Profile Distribution */}
            {Object.keys(statistics.riskProfileDistribution).length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
                  Risk Profile Distribution
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                  {Object.entries(statistics.riskProfileDistribution).map(([profile, count]) => (
                    <div key={profile} style={{
                      padding: '1rem',
                      backgroundColor: getRiskProfileColor(profile) + '10',
                      border: `2px solid ${getRiskProfileColor(profile)}`,
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        fontSize: '1.5rem', 
                        fontWeight: 'bold', 
                        color: getRiskProfileColor(profile),
                        marginBottom: '0.25rem'
                      }}>
                        {count}
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                        {profile}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters Section */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
                Filter Responses
              </h3>
              
              {/* Quick Filters */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }} className="quick-filters">
                {[
                  { key: 'all', label: 'All Responses' },
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This Week' },
                  { key: 'month', label: 'This Month' }
                ].map(filter => (
                  <button
                    key={filter.key}
                    onClick={() => handleFilterChange(filter.key)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: currentFilter === filter.key ? '#0ea5e9' : 'white',
                      color: currentFilter === filter.key ? 'white' : '#374151',
                      borderColor: currentFilter === filter.key ? '#0ea5e9' : '#d1d5db'
                    }}
                    className="quick-filter-button"
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Advanced Filters */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                alignItems: 'end'
              }} className="filters-grid">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>Search by Name/Email</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        transition: 'border-color 0.2s ease',
                        flex: 1
                      }}
                      placeholder="Enter name or email..."
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button
                      onClick={handleSearch}
                      disabled={!searchTerm.trim()}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '500',
                        cursor: searchTerm.trim() ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        backgroundColor: '#0ea5e9',
                        color: 'white',
                        opacity: searchTerm.trim() ? 1 : 0.6
                      }}
                    >
                      <Search size={16} />
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>Start Date</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s ease'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>End Date</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s ease'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <button
                    onClick={handleDateRangeFilter}
                    disabled={!dateRange.startDate || !dateRange.endDate}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '500',
                      cursor: (dateRange.startDate && dateRange.endDate) ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      opacity: (dateRange.startDate && dateRange.endDate) ? 1 : 0.6
                    }}
                  >
                    <Filter size={16} />
                    Filter by Date
                  </button>
                </div>
              </div>
            </div>

            {/* Responses Section */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }} className="responses-header">
                <div>
                  <h2 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: '#1a1a1a',
                    margin: 0
                  }}>
                    Risk Profile Responses ({responses.length})
                  </h2>
                  <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
                    Filter: {currentFilter === 'all' ? 'All responses' : 
                            currentFilter === 'search' ? `Search: "${searchTerm}"` :
                            currentFilter === 'dateRange' ? `${dateRange.startDate} to ${dateRange.endDate}` :
                            currentFilter} | Sort: {sortBy === 'newest' ? 'Latest First' : 
                                                    sortBy === 'oldest' ? 'Oldest First' :
                                                    sortBy === 'name' ? 'Name A-Z' :
                                                    sortBy === 'score' ? 'Highest Score' : 'Latest First'}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Sort Dropdown */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                      Sort by:
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        minWidth: '120px'
                      }}
                    >
                      <option value="newest">Latest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="name">Name A-Z</option>
                      <option value="score">Highest Score</option>
                    </select>
                  </div>
                  <button
                    onClick={() => fetchResponses(currentFilter)}
                    disabled={loading}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                    Refresh
                  </button>
                  
                  <button
                    onClick={() => exportData('json')}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Download size={16} />
                    Export JSON
                  </button>

                  <button
                    onClick={() => setShowFormManager(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Settings size={16} />
                    Manage Forms
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {loading && (
                <div style={{ 
                  padding: '3rem', 
                  textAlign: 'center', 
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                  <RefreshCw size={20} className="animate-spin" />
                  Loading responses...
                </div>
              )}

              {/* Responses List */}
              {!loading && (
                <div style={{ padding: '1.5rem' }}>
                  {responses.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '3rem',
                      color: '#6b7280'
                    }}>
                      <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                        No responses found
                      </h3>
                      <p>
                        {currentFilter === 'all' 
                          ? 'No risk profile responses have been submitted yet.'
                          : 'No responses match your current filter criteria.'}
                      </p>
                    </div>
                  ) : (
                    responses.map((response) => (
                      <div 
                        key={response.id} 
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          marginBottom: '1rem',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          // Highlight recent responses
                          ...(isRecentResponse(response.createdAt) ? {
                            borderColor: '#10b981',
                            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.1)'
                          } : {})
                        }}
                        className="response-card"
                      >
                        <div 
                          style={{
                            padding: '1rem',
                            backgroundColor: isRecentResponse(response.createdAt) ? '#ecfdf5' : '#f9fafb',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => setExpandedResponse(
                            expandedResponse === response.id ? null : response.id
                          )}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                            <div>
                              <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginBottom: '0.25rem'
                              }}>
                                <span style={{
                                  fontSize: '1.125rem', 
                                  fontWeight: '600', 
                                  color: '#1a1a1a'
                                }}>
                                  {response.firstName} {response.lastName}
                                </span>
                                {isRecentResponse(response.createdAt) && (
                                  <span style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px'
                                  }}>
                                    NEW
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                fontSize: '0.875rem', 
                                color: '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem'
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <Mail size={14} />
                                  {response.email}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <Phone size={14} />
                                  {response.phone}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <Clock size={14} />
                                  {isRecentResponse(response.createdAt) ? (
                                    <span style={{ color: '#10b981', fontWeight: '500' }}>
                                      {Math.round((new Date() - new Date(response.createdAt)) / (1000 * 60 * 60))}h ago
                                    </span>
                                  ) : (
                                    new Date(response.createdAt).toLocaleDateString()
                                  )}
                                </span>
                              </div>
                            </div>
                            
                            <div style={{ 
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              backgroundColor: getRiskProfileColor(response.riskProfile) + '20',
                              border: `2px solid ${getRiskProfileColor(response.riskProfile)}`,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: getRiskProfileColor(response.riskProfile)
                            }}>
                              {response.riskProfile}
                            </div>
                            
                            <div style={{ 
                              fontSize: '1.25rem', 
                              fontWeight: 'bold',
                              color: '#0ea5e9',
                              minWidth: '60px',
                              textAlign: 'center'
                            }}>
                              Score: {response.totalScore}
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showResponseDetails(response.id);
                                }}
                                style={{
                                  padding: '0.5rem',
                                  border: 'none',
                                  borderRadius: '6px',
                                  backgroundColor: '#eff6ff',
                                  color: '#0ea5e9',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '32px',
                                  height: '32px',
                                  transition: 'background-color 0.2s ease'
                                }}
                                title="View Details"
                                className="action-button"
                              >
                                <Eye size={16} />
                              </button>
                            </div>
                            
                            {expandedResponse === response.id ? (
                              <ChevronUp size={20} color="#6b7280" />
                            ) : (
                              <ChevronDown size={20} color="#6b7280" />
                            )}
                          </div>
                        </div>

                        {/* Expanded Content */}
                        {expandedResponse === response.id && (
                          <div style={{ padding: '1rem', backgroundColor: 'white' }}>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                              gap: '1rem',
                              marginBottom: '1rem'
                            }}>
                              <div>
                                <strong style={{ color: '#374151' }}>Risk Profile:</strong>
                                <div style={{ 
                                  color: getRiskProfileColor(response.riskProfile),
                                  fontWeight: '600',
                                  marginTop: '0.25rem'
                                }}>
                                  {response.riskProfile}
                                </div>
                              </div>
                              <div>
                                <strong style={{ color: '#374151' }}>Allocation:</strong>
                                <div style={{ marginTop: '0.25rem' }}>{response.allocation}</div>
                              </div>
                              <div>
                                <strong style={{ color: '#374151' }}>Total Score:</strong>
                                <div style={{ 
                                  fontSize: '1.25rem',
                                  fontWeight: 'bold',
                                  color: '#0ea5e9',
                                  marginTop: '0.25rem'
                                }}>
                                  {response.totalScore}
                                </div>
                              </div>
                              <div>
                                <strong style={{ color: '#374151' }}>Submitted:</strong>
                                <div style={{ marginTop: '0.25rem' }}>
                                  {new Date(response.createdAt).toLocaleString()}
                                </div>
                              </div>
                            </div>

                            <div style={{ 
                              padding: '1rem',
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <strong style={{ color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                                Quick Summary:
                              </strong>
                              <p style={{ 
                                margin: 0, 
                                color: '#6b7280', 
                                fontSize: '0.875rem',
                                lineHeight: 1.5
                              }}>
                                {response.firstName} scored {response.totalScore} points and was classified as 
                                <strong style={{ color: getRiskProfileColor(response.riskProfile) }}>
                                  {' '}{response.riskProfile}
                                </strong> investor with allocation of {response.allocation}.
                                Response submitted on {new Date(response.createdAt).toLocaleDateString()}.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default RiskProfileAdminDashboard;